import { useEffect } from "react";
import { DEFAULT_LOCALE, ENABLED_LOCALES, INDEXED_LOCALES, isLocaleIndexed, isTranslationIndexable, localizedPath, parsePath } from "@/lib/i18n/locales";
import { enforceKeyword, getAllKeywords, resolveKeyword } from "@/lib/i18n/seoKeywords";
import { countriesForLocale, hreflangRegionTags, ogLocaleAlternates } from "@/lib/i18n/geoTargets";
import { uiText } from "@/lib/i18n/uiText";

/**
 * Statlign SEO Head Manager
 *
 * PRINCIPLES
 * ---------------------------------------------------------------------------
 * 1. Every indexable page gets a self-referencing canonical URL.
 * 2. Every localized equivalent receives hreflang.
 * 3. x-default points to the default-language equivalent.
 * 4. SEO metadata must describe the actual page, not an unrelated keyword pool.
 * 5. Keyword metadata is treated as auxiliary only.
 * 6. Country names are NOT blindly appended to descriptions.
 * 7. JSON-LD is localized without overwriting explicit schema values.
 * 8. Private/utility pages can explicitly receive noindex,follow.
 * 9. Managed head tags are cleaned between SPA navigations.
 * 10. URLs are always normalized to statlign.com.
 *
 * IMPORTANT
 * ---------------------------------------------------------------------------
 * Search engines do not need a giant meta-keyword list to understand Statlign.
 * The strongest signals should come from:
 *
 * - page title
 * - visible H1/H2 content
 * - page body
 * - internal links
 * - canonical URL
 * - hreflang
 * - structured data
 * - entity relationships
 * - crawlable HTML
 * - performance
 * - backlinks
 *
 * This hook therefore avoids artificially stuffing unrelated keywords into
 * every page.
 */

const BASE_URL = "https://statlign.com";
const SITE_NAME = "Statlign";

const JSONLD_ID = "statlign-seo-jsonld";

/**
 * Only these meta/link nodes are managed by this hook.
 *
 * data-statlign-seo allows us to clean up stale tags safely during SPA
 * navigation without deleting tags owned by other parts of the application.
 */
const MANAGED_ATTR = "data-statlign-seo";

interface SEOOptions {
  /** Search-engine-facing title. */
  title: string;

  /** Search-engine-facing description. */
  description?: string;

  /** Optional social title. */
  ogTitle?: string;

  /** Optional social description. */
  ogDescription?: string;

  /**
   * Canonical/root route.
   *
   * Examples:
   * /predictions
   * /predictions/arsenal-vs-chelsea
   * /leagues/premier-league
   */
  canonicalPath?: string;

  /** Schema.org JSON-LD object or array of objects. */
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];

  /** Absolute or root-relative social preview image. */
  image?: string;

  /** Social preview image alt text. */
  imageAlt?: string;

  /** Open Graph type. */
  ogType?: "website" | "article";

  /**
   * Optional page-specific keywords.
   *
   * These should be tightly relevant to the page.
   * Do NOT pass hundreds of unrelated terms.
   */
  keywords?: string;

  /** Article publication timestamp. */
  publishedTime?: string;

  /** Article modification timestamp. */
  modifiedTime?: string;

  /** Explicitly prevent indexing. */
  noindex?: boolean;

  /**
   * Optional robots override.
   *
   * Example:
   * "noindex,nofollow"
   *
   * If omitted, noindex/follow or index/follow is generated automatically.
   */
  robots?: string;

  /**
   * Optional author name for article pages.
   */
  author?: string;

  /**
   * Optional article section.
   */
  articleSection?: string;

  /**
   * Optional article tags.
   *
   * Keep these genuinely relevant to the article.
   */
  articleTags?: string[];

  /**
   * Optional explicit locale override.
   *
   * Normally the locale is detected from the URL.
   */
  locale?: string;
}

/* -------------------------------------------------------------------------- */
/* URL UTILITIES                                                              */
/* -------------------------------------------------------------------------- */

function absoluteUrl(path: string): string {
  if (!path) return BASE_URL;

  if (/^https?:\/\//i.test(path)) {
    return path;
  }

  return `${BASE_URL}${path.startsWith("/") ? path : `/${path}`}`;
}

/**
 * Remove trailing slash except for the root.
 */
function normalizePath(path: string): string {
  const clean = path.trim();

  if (!clean || clean === "/") {
    return "/";
  }

  return clean.replace(/\/+$/, "");
}

/**
 * Statlign currently treats /teams/:slug and /clubs/:slug as the same entity.
 *
 * Keep this normalization here so canonical, hreflang and OG URLs remain
 * consistent.
 */
function normalizeEntityPath(path: string): string {
  return normalizePath(path.replace(/^\/teams\/(?=.)/, "/clubs/"));
}

/* -------------------------------------------------------------------------- */
/* HEAD MANAGEMENT                                                            */
/* -------------------------------------------------------------------------- */

function selectorForMeta(attr: "name" | "property", key: string): string {
  return `meta[${attr}="${CSS.escape(key)}"][${MANAGED_ATTR}]`;
}

/**
 * Adopt-then-create. The SSR/prerendered head already carries sitewide
 * defaults (description, og:*, twitter:*) rendered by the root route. Creating
 * a second managed tag for the same key would emit duplicate, conflicting
 * metadata, so we first claim any pre-existing tag for this key, and only
 * create a new element when the page has none. Extra same-key duplicates are
 * pruned so exactly one tag per key survives.
 */
function claimMeta(attr: "name" | "property", key: string): HTMLMetaElement | null {
  const all = Array.from(
    document.head.querySelectorAll<HTMLMetaElement>(`meta[${attr}="${CSS.escape(key)}"]`),
  );
  if (all.length === 0) return null;
  const managed = all.find((el) => el.hasAttribute(MANAGED_ATTR)) ?? all[0]!;
  managed.setAttribute(MANAGED_ATTR, "");
  for (const el of all) if (el !== managed) el.remove();
  return managed;
}

function upsertMeta(attr: "name" | "property", key: string, content: string | undefined | null): void {
  if (!content) return;

  let el = claimMeta(attr, key);

  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    el.setAttribute(MANAGED_ATTR, "");
    document.head.appendChild(el);
  }

  el.setAttribute("content", content);
}

function removeMeta(attr: "name" | "property", key: string): void {
  document.head.querySelectorAll(selectorForMeta(attr, key)).forEach((el) => el.remove());
}

function upsertLink(rel: string, href: string, extraAttributes?: Record<string, string>): void {
  const all = Array.from(
    document.head.querySelectorAll<HTMLLinkElement>(`link[rel="${CSS.escape(rel)}"]`),
  );
  let el = all.length ? all.find((node) => node.hasAttribute(MANAGED_ATTR)) ?? all[0]! : null;

  if (el) {
    el.setAttribute(MANAGED_ATTR, "");
    for (const node of all) if (node !== el) node.remove();
  } else {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    el.setAttribute(MANAGED_ATTR, "");
    document.head.appendChild(el);
  }

  el.setAttribute("href", href);

  if (extraAttributes) {
    for (const [key, value] of Object.entries(extraAttributes)) {
      el.setAttribute(key, value);
    }
  }
}

function removeManagedHeadElements(): void {
  document.head.querySelectorAll(`[${MANAGED_ATTR}]`).forEach((node) => node.remove());
}

/* -------------------------------------------------------------------------- */
/* TEXT NORMALIZATION                                                         */
/* -------------------------------------------------------------------------- */

function cleanText(value?: string): string {
  return (value ?? "").replace(/\s+/g, " ").trim();
}

function dedupe(values: string[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];

  for (const value of values) {
    const cleaned = cleanText(value);

    if (!cleaned) continue;

    const key = cleaned.toLocaleLowerCase();

    if (seen.has(key)) continue;

    seen.add(key);
    result.push(cleaned);
  }

  return result;
}

/**
 * Keep titles concise without destroying the main topic.
 *
 * We do NOT blindly cut at 60 characters because 60 is not a hard Google
 * limit. Google may rewrite titles based on width, device and query.
 *
 * Instead, this is a practical safeguard against extremely long titles.
 */
function optimizeTitle(title: string): string {
  const cleaned = cleanText(title);

  if (cleaned.length <= 65) {
    return cleaned;
  }

  const separators = [" | ", " — ", " – ", " · ", " - "];

  for (const separator of separators) {
    const index = cleaned.lastIndexOf(separator);

    if (index >= 35 && index <= 65) {
      return cleaned.slice(0, index).trim();
    }
  }

  const candidate = cleaned.slice(0, 65);
  const lastSpace = candidate.lastIndexOf(" ");

  return (lastSpace > 40 ? candidate.slice(0, lastSpace) : candidate).trim();
}

/**
 * Description safeguard.
 *
 * Google does not enforce a fixed 160-character limit, so this is deliberately
 * less aggressive than the previous implementation.
 */
function optimizeDescription(description: string): string {
  const cleaned = cleanText(description);

  if (cleaned.length <= 160) {
    return cleaned;
  }

  const candidate = cleaned.slice(0, 160);
  const lastSpace = candidate.lastIndexOf(" ");

  return (lastSpace > 100 ? candidate.slice(0, lastSpace) : candidate).trim();
}

/* -------------------------------------------------------------------------- */
/* HREFLANG                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Replace all hreflang links managed by Statlign.
 *
 * IMPORTANT:
 * ---------------------------------------------------------------------------
 * Language-level hreflang:
 *
 * en
 * fr
 * de
 * etc.
 *
 * Region-specific hreflang is only added when Statlign's geo configuration
 * explicitly defines a region variant.
 *
 * We do NOT pretend that:
 *
 * es-MX
 * es-ES
 * es-AR
 *
 * are separate pages if they all point to exactly the same page.
 *
 * Language targeting and geographic targeting should be based on actual
 * localized content/URLs.
 */
function setHreflangAlternates(rootPath: string): void {
  document.querySelectorAll(`link[rel="alternate"][hreflang]`).forEach((node) => node.remove());

  const fragment = document.createDocumentFragment();
  const emitted = new Set<string>();

  const add = (hreflang: string, href: string): void => {
    const normalized = hreflang.trim();

    if (!normalized || emitted.has(normalized)) {
      return;
    }

    emitted.add(normalized);

    const link = document.createElement("link");

    link.setAttribute("rel", "alternate");
    link.setAttribute("hreflang", normalized);
    link.setAttribute("href", href);
    link.setAttribute(MANAGED_ATTR, "");

    fragment.appendChild(link);
  };

  const cluster = ENABLED_LOCALES.filter((l) => isTranslationIndexable(l.code, rootPath));
  for (const locale of cluster.length ? cluster : INDEXED_LOCALES) {
    const href = absoluteUrl(localizedPath(locale.code, rootPath));

    /*
     * Primary language-level alternate.
     */
    add(locale.hreflang, href);

    /*
     * Optional region-specific alternates.
     *
     * Only use these if geoTargets explicitly defines them.
     */
    for (const region of hreflangRegionTags(locale.code)) {
      add(region, href);
    }
  }

  /*
   * x-default should point to the default-language version.
   */
  add("x-default", absoluteUrl(localizedPath(DEFAULT_LOCALE, rootPath)));

  document.head.appendChild(fragment);
}

/* -------------------------------------------------------------------------- */
/* OPEN GRAPH LOCALES                                                         */
/* -------------------------------------------------------------------------- */

function setOgLocaleAlternates(activeCode: string): void {
  document.querySelectorAll(`meta[property="og:locale:alternate"][${MANAGED_ATTR}]`).forEach((node) => node.remove());

  const fragment = document.createDocumentFragment();
  const emitted = new Set<string>();

  const add = (locale: string): void => {
    const value = cleanText(locale);

    if (!value || emitted.has(value)) {
      return;
    }

    emitted.add(value);

    const meta = document.createElement("meta");

    meta.setAttribute("property", "og:locale:alternate");

    meta.setAttribute("content", value);
    meta.setAttribute(MANAGED_ATTR, "");

    fragment.appendChild(meta);
  };

  for (const locale of ENABLED_LOCALES) {
    if (locale.code !== activeCode) {
      add(locale.intlLocale);
    }
  }

  for (const locale of ogLocaleAlternates(activeCode)) {
    add(locale);
  }

  document.head.appendChild(fragment);
}

/* -------------------------------------------------------------------------- */
/* JSON-LD                                                                    */
/* -------------------------------------------------------------------------- */

const LANGUAGE_TYPES = new Set([
  "WebPage",
  "CollectionPage",
  "ItemPage",
  "AboutPage",
  "ContactPage",
  "FAQPage",
  "WebSite",
  "Article",
  "NewsArticle",
  "BlogPosting",
  "TechArticle",
  "Blog",
  "CreativeWork",
  "Dataset",
  "Report",
  "Guide",
  "HowTo",
  "VideoObject",
  "ImageObject",
]);

const AUDIENCE_TYPES = new Set([
  "WebPage",
  "CollectionPage",
  "ItemPage",
  "AboutPage",
  "ContactPage",
  "FAQPage",
  "WebSite",
  "Article",
  "NewsArticle",
  "BlogPosting",
  "TechArticle",
  "Blog",
  "CreativeWork",
  "Dataset",
  // NOTE: `Product` is deliberately absent. Google's product/merchant-listing
  // validation reads `audience` as a demographic `PeopleAudience`, so a
  // geographic `Audience` there is reported as an invalid object type.
  "Service",

  "Organization",
  "SportsOrganization",
]);

function nodeTypes(obj: Record<string, unknown>): string[] {
  const type = obj["@type"];

  if (typeof type === "string") {
    return [type];
  }

  if (Array.isArray(type)) {
    return type.filter((value): value is string => typeof value === "string");
  }

  return [];
}

/**
 * Recursively localize supported Schema.org nodes.
 *
 * Explicit values are never overwritten.
 */
function localizeJsonLd(node: unknown, language: string): unknown {
  if (Array.isArray(node)) {
    return node.map((item) => localizeJsonLd(item, language));
  }

  if (!node || typeof node !== "object") {
    return node;
  }

  const obj = node as Record<string, unknown>;

  const result: Record<string, unknown> = {
    ...obj,
  };

  if (!("inLanguage" in obj) && nodeTypes(obj).some((type) => LANGUAGE_TYPES.has(type))) {
    result["inLanguage"] = language;
  }

  for (const key of Object.keys(result)) {
    const value = result[key];

    if (value && typeof value === "object") {
      result[key] = localizeJsonLd(value, language);
    }
  }

  return result;
}

/**
 * Add audience only where the caller's schema type supports it.
 *
 * Existing audience declarations are respected.
 */
function injectAudience(node: unknown, locale: string): unknown {
  const countries = countriesForLocale(locale);

  if (!countries.length) {
    return node;
  }

  const audience = {
    "@type": "Audience",
    audienceType: "Sports fans, bettors and analysts",

    geographicArea: countries.map((country) => ({
      "@type": "Country",
      name: country.name,
      identifier: `ISO 3166-1:${country.iso}`,
    })),
  };

  const apply = (value: unknown): unknown => {
    if (!value || typeof value !== "object" || Array.isArray(value)) {
      return value;
    }

    const object = value as Record<string, unknown>;

    if ("audience" in object) {
      return object;
    }

    if (!nodeTypes(object).some((type) => AUDIENCE_TYPES.has(type))) {
      return object;
    }

    return {
      ...object,
      audience,
    };
  };

  if (Array.isArray(node)) {
    return node.map(apply);
  }

  return apply(node);
}

/* -------------------------------------------------------------------------- */
/* JSON-LD SAFETY                                                             */
/* -------------------------------------------------------------------------- */

/**
 * Prevent accidental circular structures from breaking the head.
 */
function safeJsonStringify(value: unknown): string | null {
  try {
    return JSON.stringify(value);
  } catch {
    return null;
  }
}

/* -------------------------------------------------------------------------- */
/* KEYWORD HANDLING                                                           */
/* -------------------------------------------------------------------------- */

/**
 * Build a small, relevant keyword set.
 *
 * IMPORTANT:
 * The keywords meta tag is NOT treated as a major ranking mechanism.
 *
 * We keep this intentionally constrained so Statlign does not create a huge
 * irrelevant metadata footprint.
 */
function buildKeywords(
  activeLocale: string,
  rootPath: string,
  explicitKeywords?: string,
  articleTags?: string[],
): string {
  const values: string[] = [];

  if (explicitKeywords) {
    values.push(...explicitKeywords.split(",").map(cleanText));
  }

  if (articleTags?.length) {
    values.push(...articleTags);
  }

  /*
   * Existing Statlign keyword resolver.
   *
   * Only take a small number of highly relevant keywords.
   */
  try {
    const resolved = getAllKeywords(activeLocale, rootPath);

    values.push(...resolved.slice(0, 12));
  } catch {
    // Keyword metadata is non-critical.
  }

  try {
    const pool = resolveKeyword(activeLocale, rootPath);

    if (pool?.keyword) {
      values.push(pool.keyword);
    }
  } catch {
    // Non-critical.
  }

  return dedupe(values).slice(0, 20).join(", ");
}

/* -------------------------------------------------------------------------- */
/* ARTICLE META                                                               */
/* -------------------------------------------------------------------------- */

function setArticleMetadata(options: SEOOptions, mergedKeywords: string): void {
  if (options.ogType !== "article") {
    removeMeta("property", "article:published_time");

    removeMeta("property", "article:modified_time");

    removeMeta("property", "article:author");

    removeMeta("property", "article:section");

    removeMeta("property", "article:tag");

    removeMeta("name", "news_keywords");

    return;
  }

  if (options.publishedTime) {
    upsertMeta("property", "article:published_time", options.publishedTime);
  }

  if (options.modifiedTime) {
    upsertMeta("property", "article:modified_time", options.modifiedTime);
  }

  if (options.author) {
    upsertMeta("property", "article:author", options.author);
  }

  if (options.articleSection) {
    upsertMeta("property", "article:section", options.articleSection);
  }

  if (options.articleTags?.length) {
    const tags = dedupe(options.articleTags).join(", ");

    upsertMeta("property", "article:tag", tags);
  }

  /*
   * news_keywords is kept only as a compatibility field.
   *
   * It should never be treated as a replacement for actual article content,
   * headlines, entities or structured data.
   */
  if (mergedKeywords) {
    upsertMeta("name", "news_keywords", mergedKeywords);
  }
}

/* -------------------------------------------------------------------------- */
/* MAIN HOOK                                                                  */
/* -------------------------------------------------------------------------- */

export function useSEO(options: SEOOptions): void {
  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    /*
     * Detect active locale from URL unless explicitly overridden.
     */
    const parsed = parsePath(window.location.pathname);

    const activeLocale = options.locale ?? parsed.locale ?? DEFAULT_LOCALE;

    /*
     * Determine the actual route.
     */
    const rawPath = options.canonicalPath ?? parsed.path ?? "/";

    /*
     * Normalize entity aliases.
     */
    const rootPath = normalizeEntityPath(rawPath);

    /*
     * Resolve locale definition.
     */
    const localeDefinition =
      ENABLED_LOCALES.find((locale) => locale.code === activeLocale) ??
      ENABLED_LOCALES.find((locale) => locale.code === DEFAULT_LOCALE) ??
      ENABLED_LOCALES[0];

    /*
     * -----------------------------------------------------------------------
     * TITLE / DESCRIPTION
     * -----------------------------------------------------------------------
     */

    let effectiveTitle = cleanText(uiText(options.title));

    let effectiveDescription = cleanText(options.description ? uiText(options.description) : undefined);

    /*
     * Use Statlign's existing keyword resolver as a lightweight relevance
     * guard, not as a mechanism for injecting huge keyword lists.
     */
    try {
      const resolved = resolveKeyword(activeLocale, rootPath);

      if (resolved?.keyword) {
        const enforced = enforceKeyword(effectiveTitle, effectiveDescription, resolved.keyword);

        effectiveTitle = cleanText(enforced.title);

        effectiveDescription = cleanText(enforced.description);
      }
    } catch {
      // SEO metadata should never crash page rendering.
    }

    /*
     * Keep title concise.
     */
    effectiveTitle = optimizeTitle(effectiveTitle);

    /*
     * Keep description concise enough for normal SERP presentation.
     */
    effectiveDescription = optimizeDescription(effectiveDescription);

    /*
     * -----------------------------------------------------------------------
     * ROBOTS
     * -----------------------------------------------------------------------
     */

    const robots = !isTranslationIndexable(activeLocale, rootPath)
      ? "noindex,follow"
      : options.robots ?? (options.noindex ? "noindex,follow" : "index,follow");

    upsertMeta("name", "robots", robots);

    /*
     * -----------------------------------------------------------------------
     * TITLE
     * -----------------------------------------------------------------------
     */

    document.title = effectiveTitle;

    /*
     * -----------------------------------------------------------------------
     * CANONICAL
     * -----------------------------------------------------------------------
     */

    const canonical = absoluteUrl(localizedPath(localeDefinition!.code, rootPath));

    upsertLink("canonical", canonical);

    /*
     * -----------------------------------------------------------------------
     * HREFLANG
     * -----------------------------------------------------------------------
     */

    setHreflangAlternates(rootPath);

    /*
     * -----------------------------------------------------------------------
     * DESCRIPTION
     * -----------------------------------------------------------------------
     */

    if (effectiveDescription) {
      upsertMeta("name", "description", effectiveDescription);
    } else {
      removeMeta("name", "description");
    }

    /*
     * -----------------------------------------------------------------------
     * OPEN GRAPH
     * -----------------------------------------------------------------------
     */

    const socialTitle = cleanText(options.ogTitle || effectiveTitle);

    const socialDescription = cleanText(options.ogDescription || effectiveDescription);

    upsertMeta("property", "og:title", socialTitle);

    upsertMeta("property", "og:description", socialDescription);

    upsertMeta("property", "og:url", canonical);

    upsertMeta("property", "og:type", options.ogType ?? "website");

    upsertMeta("property", "og:site_name", SITE_NAME);

    upsertMeta("property", "og:locale", localeDefinition!.intlLocale);

    setOgLocaleAlternates(localeDefinition!.code);

    /*
     * -----------------------------------------------------------------------
     * SOCIAL IMAGE
     * -----------------------------------------------------------------------
     */

    if (options.image) {
      const imageUrl = absoluteUrl(options.image);

      const imageAlt = cleanText(options.imageAlt || effectiveTitle);

      upsertMeta("property", "og:image", imageUrl);

      upsertMeta("property", "og:image:secure_url", imageUrl);

      upsertMeta("property", "og:image:type", /\.png$/i.test(imageUrl) ? "image/png" : "image/jpeg");

      upsertMeta("property", "og:image:alt", imageAlt);

      upsertMeta("name", "twitter:card", "summary_large_image");

      upsertMeta("name", "twitter:image", imageUrl);

      upsertMeta("name", "twitter:image:alt", imageAlt);
    } else {
      /*
       * Prevent a previous page's social image from leaking into the next
       * SPA route.
       */
      removeMeta("property", "og:image");

      removeMeta("property", "og:image:secure_url");

      removeMeta("property", "og:image:type");

      removeMeta("property", "og:image:alt");

      removeMeta("name", "twitter:image");

      removeMeta("name", "twitter:image:alt");

      upsertMeta("name", "twitter:card", "summary");
    }

    upsertMeta("name", "twitter:title", socialTitle);

    upsertMeta("name", "twitter:description", socialDescription);

    upsertMeta("name", "twitter:url", canonical);

    /*
     * -----------------------------------------------------------------------
     * KEYWORDS
     * -----------------------------------------------------------------------
     */

    const mergedKeywords = buildKeywords(activeLocale, rootPath, options.keywords, options.articleTags);

    if (mergedKeywords) {
      upsertMeta("name", "keywords", mergedKeywords);
    } else {
      removeMeta("name", "keywords");
    }

    /*
     * -----------------------------------------------------------------------
     * ARTICLE / NEWS METADATA
     * -----------------------------------------------------------------------
     */

    setArticleMetadata(options, mergedKeywords);

    /*
     * -----------------------------------------------------------------------
     * JSON-LD
     * -----------------------------------------------------------------------
     */

    const existingJsonLd = document.getElementById(JSONLD_ID);

    if (existingJsonLd) {
      existingJsonLd.remove();
    }

    if (options.jsonLd) {
      const localized = localizeJsonLd(options.jsonLd, localeDefinition!.intlLocale);

      const withAudience = injectAudience(localized, localeDefinition!.code);

      const serialized = safeJsonStringify(withAudience);

      if (serialized) {
        const script = document.createElement("script");

        script.type = "application/ld+json";

        script.id = JSONLD_ID;

        script.setAttribute(MANAGED_ATTR, "");

        script.textContent = serialized;

        document.head.appendChild(script);
      }
    }

    /*
     * -----------------------------------------------------------------------
     * CLEANUP
     * -----------------------------------------------------------------------
     *
     * React can re-run this effect during SPA navigation. Remove only the
     * JSON-LD node here. Other managed tags are intentionally rewritten on
     * every invocation.
     */
    return () => {
      document.getElementById(JSONLD_ID)?.remove();
    };
  }, [
    options.title,
    options.description,
    options.ogTitle,
    options.ogDescription,
    options.canonicalPath,
    options.image,
    options.imageAlt,
    options.ogType,
    options.keywords,
    options.publishedTime,
    options.modifiedTime,
    options.noindex,
    options.robots,
    options.author,
    options.articleSection,
    JSON.stringify(options.articleTags ?? []),
    JSON.stringify(options.jsonLd ?? null),
    options.locale,
  ]);
}
